import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
} from "react";
import type { MouseEventHandler } from "react";

import cn from "classnames";

import { DropdownProvider, useDropdownStore } from "./store/store";
import { DROPDOWN_DISPLAY_NAME } from "./utils/core";
import { createTestIdForDropdownElement } from "./utils/getTestId";
import { getChildrenRecord } from "../../utils/react";

import type { IDropdownRef, IDropdown, IDropdownMainProps } from "./types";

import classes from "./Dropdown.styl";

export const DROPDOWN_TEST_ID = createTestIdForDropdownElement("root");

/**
 * Композитный компонент для реализации доступных выпадающих окон.
 *
 * <a href="https://www.figma.com/design/SZCPsHnuqBIWlYSyfmq0j1/%F0%9F%A7%A9-Sheets-%26-Modules-%26-Wrappers?node-id=287-96139&node-type=frame&m=dev" target="_blank">Спека</a>
 * @author alexander.gordeev (alexander.gordeev@vk.team)
 * @constructor
 */
export const Dropdown = forwardRef<IDropdownRef, IDropdown>(
    (
        {
            children,
            className,
            "data-tsid": tsid = DROPDOWN_TEST_ID,
            ...options
        },
        ref,
    ) => {
        return (
            // провайдеру не нужны tsid
            // eslint-disable-next-line ok-custom-rules/check-attribute
            <DropdownProvider options={options}>
                <DropdownContent
                    className={className}
                    data-tsid={tsid}
                    ref={ref}
                >
                    {children}
                </DropdownContent>
            </DropdownProvider>
        );
    },
);

Dropdown.displayName = DROPDOWN_DISPLAY_NAME;

const DropdownContent = forwardRef<IDropdownRef, IDropdownMainProps>(
    ({ className, "data-tsid": tsid, children }, ref) => {
        const {
            options: {
                closeMenuAfterMouseLeave,
                focusToActiveElementAfterOpen,
                closeMenuAfterMouseLeaveTimeout,
            },
            containerRef,
            closeTimerRef,
            setIsOpen,
            setWithFocusOnActiveElement,
        } = useDropdownStore();

        const mouseLeaveHandler: MouseEventHandler = useCallback(() => {
            if (closeMenuAfterMouseLeave) {
                closeTimerRef.current = setTimeout(() => {
                    setIsOpen(false);
                    setWithFocusOnActiveElement(focusToActiveElementAfterOpen);
                }, closeMenuAfterMouseLeaveTimeout);
            }
        }, [
            closeTimerRef,
            closeMenuAfterMouseLeave,
            closeMenuAfterMouseLeaveTimeout,
            focusToActiveElementAfterOpen,
            setIsOpen,
            setWithFocusOnActiveElement,
        ]);

        const childrenArray = useMemo(
            () =>
                getChildrenRecord(
                    children,
                    "Dropdown",
                    {
                        // Так как выпадашки могут быть мультиуровневыми, то допускаем, что Button может быть передан неявно через Item
                        Button: {
                            optionalRequired: true,
                        },
                        Item: {
                            optionalRequired: true,
                        },
                        Menu: {
                            required: true,
                        },
                        Unknown: {
                            skipElement: true,
                        },
                    },
                    {
                        isArrayResult: true,
                    },
                ),
            [children],
        );

        useImperativeHandle(ref, () => ({
            openDropdown() {
                setIsOpen(true);
            },
            closeDropdown() {
                setIsOpen(false);
            },
        }));

        return (
            <div
                className={cn(classes["wrapper"], className)}
                onMouseLeave={mouseLeaveHandler}
                data-tsid={tsid}
                data-uikit={DROPDOWN_DISPLAY_NAME}
                ref={containerRef}
            >
                {childrenArray}
            </div>
        );
    },
);

DropdownContent.displayName = `${DROPDOWN_DISPLAY_NAME}Content`;

export default Dropdown;
